import { z } from "zod";

/**
 * Onchain Swap Domain Types
 *
 * Shared domain types for onchain swap functionality.
 * Used by both frontend (hub-v2) and backend (api).
 *
 * NOTE: API response types are in core/types/src/api/onchain-swap/
 * NOTE: Mobula-specific schemas are in the API's infrastructure layer:
 * apps/api/src/modules/onchain-swap/infrastructure/mobula/mobula.schemas.ts
 */

// Re-export API response types
export {
  // Quote types
  swapQuoteResponseSchema,
  swapQuoteTokenInfoSchema,
  swapTransactionSchema,
  swapRouteHopSchema,
  swapRouteSchema,
  type SwapQuoteResponse,
  type SwapQuoteTokenInfo,
  type SwapTransaction,
  type SwapRouteHop,
  type SwapRoute,
  // Portfolio types
  portfolioResponseSchema,
  portfolioAssetSchema,
  portfolioAssetInfoSchema,
  portfolioContractBalanceSchema,
  type PortfolioResponse,
  type PortfolioAsset,
  type PortfolioAssetInfo,
  type PortfolioContractBalance,
  // Token types
  swapTokenInfoSchema,
  type SwapTokenInfo,
} from "./api/onchain-swap";

// ============================================================================
// Domain Types
// ============================================================================

// DEX protocol identifiers
export const dexProtocolSchema = z.enum([
  "uniswap-v3",
  "aerodrome-cl",
  "aerodrome-cl-2",
  "pancake-v3",
  "pancake-v3-base",
  "pancake-infinity-bsc",
  "uniswap-v4-robinhood",
  // Solana DEXes
  "meteora",
  "meteora-dyn",
  "meteora-dyn2",
  "meteora-dbc",
  "raydium",
  "raydium-clmm",
  "raydium-cpmm",
  "raydium-launchlab",
  "orca",
  "pumpswap",
  "jupiter-studio",
]);

export const swapTokenSchema = z.object({
  address: z.string(),
  symbol: z.string(),
  name: z.string(),
  // ERC-20 `decimals` is a uint8 (0-255); 18 is common but not a ceiling
  // (e.g. NEAR-on-Ethereum uses 24). Don't cap at 18 or valid tokens get rejected.
  decimals: z.number().int().min(0).max(255),
  logo: z.string().optional(),
  chainId: z.string(), // String to support both EVM ("8453") and non-EVM ("solana")
});

export const dexConfigSchema = z.object({
  id: dexProtocolSchema,
  name: z.string(),
  logo: z.string(),
  supportedChains: z.array(z.string()), // String to support both EVM ("8453") and non-EVM ("solana")
  mobulaProtocolId: z.string(),
  description: z.string().optional(),
  /** Full exchange ID format: {protocol}|{chain} (e.g., "aerodrome-cl|base") */
  exchangeId: z.string(),
  /** Human-readable chain name (e.g., "Base", "Arbitrum") */
  chainName: z.string(),
  /** Selectable only from a campaign page: Mobula cannot route this protocol via `onlyProtocols`, so it needs a pinned pool */
  campaignOnly: z.boolean().optional(),
});

// Chain ID to blockchain name mapping for Mobula API
// Currently supported chains for onchain swap functionality
// Supports both EVM chains (numeric IDs) and non-EVM chains (string IDs like "solana")
export const CHAIN_ID_TO_BLOCKCHAIN: Record<string, string> = {
  "1": "ethereum",
  "56": "bsc",
  "4663": "robinhood-chain",
  "8453": "base",
  "42161": "arbitrum",
  "solana": "solana",
} as const;

/**
 * Get blockchain name for Mobula API from chain identifier
 * @param chainId - EVM chain ID (number or string) or non-EVM chain name (string)
 * @returns Mobula blockchain name or undefined if not supported
 *
 * @example
 * getBlockchainName(8453)     // "base" (EVM)
 * getBlockchainName("8453")   // "base" (EVM as string)
 * getBlockchainName("solana") // "solana" (non-EVM)
 */
export const getBlockchainName = (chainId: string | number): string | undefined => {
  const key = String(chainId);
  return CHAIN_ID_TO_BLOCKCHAIN[key];
};

// ============================================================================
// Token Selector Types (shared with frontend)
// ============================================================================

// Simplified response - only essential fields for token selector UI
export const tokenSelectorItemSchema = z.object({
  // Identity (required)
  address: z.string(),
  symbol: z.string(),
  name: z.string(),
  // ERC-20 `decimals` is a uint8 (0-255); 18 is common but not a ceiling
  // (e.g. NEAR-on-Ethereum uses 24). Don't cap at 18 or valid tokens get rejected.
  decimals: z.number().int().min(0).max(255),
  chainId: z.string(),

  // Optional fields
  logo: z.string().nullable().optional(),
  balance: z.string().nullable().optional(), // Raw balance for filtering
  balanceFormatted: z.string().nullable().optional(), // Display balance
  marketCap: z.number().nullable().optional(), // For sorting popular tokens
  isCampaignToken: z.boolean().optional(), // True if token is from an active campaign
  isPopular: z.boolean().optional(), // Verified, shown in the default list: popular, campaign, native, or a held token Mobula lists (asset id > 0)
});

// Keep old schema type for backwards compatibility during transition
export const tokenSelectorSourceSchema = z.enum(["wallet", "popular", "both"]);

export const getTokensForSwapResponseSchema = z.object({
  tokens: z.array(tokenSelectorItemSchema),
  metadata: z.object({
    totalCount: z.number().int(),
    walletTokensCount: z.number().int(),
    popularTokensCount: z.number().int(),
    deduplicatedCount: z.number().int(),
    chainId: z.string(), // String to support both EVM ("8453") and non-EVM ("solana")
  }),
});

// DEX pairs response schema
export const dexPairTokenSchema = z.object({
  address: z.string(),
  symbol: z.string(),
  name: z.string(),
  decimals: z.number().int(),
  logo: z.string().nullable(),
});

export const dexPairInfoSchema = z.object({
  pairAddress: z.string(),
  token0: dexPairTokenSchema,
  token1: dexPairTokenSchema,
  volume24h: z.number().optional(),
});

export const getDexPairsResponseSchema = z.object({
  pairs: z.array(dexPairInfoSchema),
  metadata: z.object({
    totalCount: z.number().int(),
    chainId: z.string(),
  }),
});

// Popular token (unified type for token selector)
export const popularTokenSchema = z.object({
  address: z.string(),
  symbol: z.string(),
  name: z.string(),
  decimals: z.number().int(),
  logoURI: z.string().optional(),
  marketCap: z.number().optional(),
  liquidity: z.number().optional(),
});

// ============================================================================
// API DTO Schemas (validation for API endpoints)
// ============================================================================

export const getSwapQuoteSchema = z.object({
  chainId: z.string(),
  tokenIn: z.string(),
  tokenOut: z.string(),
  amountRaw: z.string(),
  walletAddress: z.string().optional(),
  slippage: z.string().optional(),
  onlyProtocols: z
    .string()
    .optional()
    .transform((val) => (val ? val.split(",") : undefined))
    .pipe(z.array(dexProtocolSchema).optional()),
  /** Specific pool address to use for the swap (for campaign volume counting) */
  poolAddress: z.string().optional(),
});

export const getWalletPortfolioSchema = z.object({
  wallets: z.string().transform((val) => val.split(",")),
  blockchains: z
    .string()
    .optional()
    .transform((val) => (val ? val.split(",") : undefined)),
});

export const getTokenByAddressSchema = z.object({
  address: z.string(),
  chainId: z.string().optional(), // String to support both EVM ("8453") and non-EVM ("solana")
});

export const getTokensForSwapSchema = z.object({
  walletAddress: z.string().optional(), // Optional - if not provided, returns only popular/campaign tokens without balances
  chainId: z.string().refine((val) => Object.hasOwn(CHAIN_ID_TO_BLOCKCHAIN, val), {
    message: `Unsupported chain ID. Supported chains: ${Object.keys(CHAIN_ID_TO_BLOCKCHAIN).join(", ")}`,
  }),
  dexProtocol: dexProtocolSchema.optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional(),
});

// ============================================================================
// Inferred TypeScript Types
// ============================================================================

export type DexProtocol = z.infer<typeof dexProtocolSchema>;
export type SwapToken = z.infer<typeof swapTokenSchema>;
export type DexConfig = z.infer<typeof dexConfigSchema>;
export type TokenSelectorSource = z.infer<typeof tokenSelectorSourceSchema>;
export type TokenSelectorItem = z.infer<typeof tokenSelectorItemSchema>;
export type GetTokensForSwapResponse = z.infer<typeof getTokensForSwapResponseSchema>;
export type DexPairToken = z.infer<typeof dexPairTokenSchema>;
export type DexPairInfo = z.infer<typeof dexPairInfoSchema>;
export type GetDexPairsResponse = z.infer<typeof getDexPairsResponseSchema>;
export type GetSwapQuote = z.infer<typeof getSwapQuoteSchema>;
export type GetWalletPortfolio = z.infer<typeof getWalletPortfolioSchema>;
export type GetTokenByAddress = z.infer<typeof getTokenByAddressSchema>;
export type GetTokensForSwap = z.infer<typeof getTokensForSwapSchema>;
export type PopularToken = z.infer<typeof popularTokenSchema>;
